<template>
  <div class="checkm2-container">
    <div class="content-section">
      <h1>Genome Quality Assessment (CheckM2)</h1>
      
      <div class="description">
        <h2>About CheckM2</h2>
        <p>CheckM2 is a tool that uses machine learning to quickly assess genome quality. It can predict the completeness and contamination of metagenome-assembled genomes (MAGs) and single-cell amplified genomes (SAGs).</p>
        
        <p class="additional-info">
          Unlike its predecessor, CheckM2 employs universally trained machine learning models that work independently of taxonomic lineage. This approach allows it to effectively analyze organisms with reduced genomes or unusual biology, such as Nanoarchaeota or Patescibacteria. The tool uses two distinct machine learning models:
        </p>
        <ul class="model-list">
          <li><strong>General Model:</strong> A gradient boost model for organisms not well represented in databases (novel at order, class, or phylum level)</li>
          <li><strong>Specific Model:</strong> A neural network model for organisms closely related to the reference training set (known species, genus, or family)</li>
        </ul>
        
        <el-alert
          title="Processing Time Note"
          type="info"
          :closable="false"
          show-icon
        >
          <p>Analysis may take several minutes depending on genome size. Please be patient.</p>
        </el-alert>

        <div class="features">
          <h3>Key Features:</h3>
          <ul>
            <li>Uses machine learning models for quality assessment</li>
            <li>Suitable for any bacterial or archaeal genome</li>
            <li>Provides completeness and contamination estimates</li>
            <li>Fast and accurate analysis</li>
            <li>Effective for both typical and reduced genomes</li>
            <li>Automatic model selection based on genome characteristics</li>
          </ul>
        </div>
      </div>

      <div class="upload-section">
        <h2>Upload Genome File</h2>
        <p class="upload-info">Please upload a genome file in FASTA format (.fa, .fasta, .fna). Maximum file size: 100MB.</p>
        
        <el-upload
          class="upload-demo"
          drag
          action="/api/tools/checkm2/analyze/"
          :on-success="handleSuccess"
          :on-error="handleError"
          :before-upload="beforeUpload"
          :on-progress="handleProgress"
          :multiple="false"
          :limit="1"
        >
          <el-icon class="el-icon--upload"><upload-filled /></el-icon>
          <div class="el-upload__text">
            Drop file here or <em>click to upload</em>
          </div>
          <template #tip>
            <div class="el-upload__tip">
              Supported file types: .fa, .fasta, .fna (max 100MB)
            </div>
          </template>
        </el-upload>

        <!-- Upload Progress Display -->
        <div v-if="uploading" class="progress-section">
          <el-progress 
            :percentage="uploadProgress" 
            :status="uploadProgress === 100 ? 'success' : ''"
            :format="progressFormat"
          />
          <p class="progress-text">{{ progressText }}</p>
        </div>
      </div>

      <!-- Results Display Section -->
      <div v-if="results" class="results-section">
        <div class="results-header">
          <h2>Analysis Results</h2>
          <el-button 
            type="primary" 
            @click="downloadResults" 
            :disabled="!resultFileUrl"
            :loading="downloading"
          >
            Download Results (TSV)
          </el-button>
        </div>

        <el-table
          :data="parsedResults"
          style="width: 100%"
          border
          stripe
          :default-sort="{ prop: 'completeness', order: 'descending' }"
        >
          <el-table-column
            prop="name"
            label="Genome Name"
            min-width="200"
            sortable
          />
          <el-table-column
            prop="completeness"
            label="Completeness"
            width="150"
            sortable
          >
            <template #default="scope">
              <div class="quality-indicator">
                <el-tooltip
                  :content="getCompletenessTooltip(scope.row.completeness)"
                  placement="top"
                >
                  <div>
                    <el-progress
                      type="dashboard"
                      :percentage="Number(scope.row.completeness)"
                      :color="getCompletenessColor(scope.row.completeness)"
                      :width="50"
                    />
                    <span>{{ scope.row.completeness }}%</span>
                  </div>
                </el-tooltip>
              </div>
            </template>
          </el-table-column>
          <el-table-column
            prop="contamination"
            label="Contamination"
            width="150"
            sortable
          >
            <template #default="scope">
              <div class="quality-indicator">
                <el-tooltip
                  :content="getContaminationTooltip(scope.row.contamination)"
                  placement="top"
                >
                  <div>
                    <el-progress
                      type="dashboard"
                      :percentage="Number(scope.row.contamination)"
                      :color="getContaminationColor(scope.row.contamination)"
                      :width="50"
                    />
                    <span>{{ scope.row.contamination }}%</span>
                  </div>
                </el-tooltip>
              </div>
            </template>
          </el-table-column>
          <el-table-column
            prop="quality"
            label="Quality Level"
            width="150"
          >
            <template #default="scope">
              <el-tooltip
                :content="getQualityTooltip(scope.row.quality)"
                placement="top"
              >
                <el-tag :type="getQualityTagType(scope.row.quality)">
                  {{ getQualityLabel(scope.row.quality) }}
                </el-tag>
              </el-tooltip>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
    
    <!-- Citation and License Section -->
    <div class="citation-section">
      <h3>Citation and License</h3>
      <div class="citation-content">
        <p class="citation-text">
          This tool integrates CheckM2, developed by Chklovski et al. If you use this tool in your research, please cite:
        </p>
        <div class="citation-box">
          <p class="paper-citation">
            <strong>Primary Citation:</strong><br>
            Chklovski, A., Parks, D.H., Woodcroft, B.J. et al. Parks DH, Hugenholtz P. (2023). 
            CheckM2: a rapid, scalable and accurate tool for assessing microbial genome quality using machine learning. 
            <em>Nature Methods 20</em>, 1203-1212 (2023). 
            <a href="https://doi.org/10.1038/s41592-023-01940-w" target="_blank">https://doi.org/10.1038/s41592-023-01940-w</a>
          </p>
          <p class="repo-citation">
            <strong>Software Repository:</strong><br>
            CheckM2 GitHub Repository: <a href="https://github.com/chklovski/CheckM2" target="_blank">https://github.com/chklovski/CheckM2</a>
          </p>
        </div>
        <div class="license-info">
          <p class="license-text">
            CheckM2 is licensed under the GNU General Public License v3.0 (GPL-3.0).
          </p>
          <p class="usage-note">
            This is an integration of CheckM2 and is not officially affiliated with the CheckM2 project. For the official implementation and latest updates, please visit the CheckM2 GitHub repository.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { UploadFilled } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'

export default {
  name: 'CheckM2',
  components: {
    UploadFilled
  },
  setup() {
    const results = ref(null)
    const parsedResults = ref([])
    const uploading = ref(false)
    const uploadProgress = ref(0)
    const progressText = ref('')
    const resultFileUrl = ref(null)
    const downloading = ref(false)

    const beforeUpload = (file) => {
      const isValidType = ['fa', 'fasta', 'fna'].includes(
        file.name.split('.').pop().toLowerCase()
      )
      const isLt100M = file.size / 1024 / 1024 < 100

      if (!isValidType) {
        ElMessage.error('Please upload a FASTA format file!')
        return false
      }
      if (!isLt100M) {
        ElMessage.error('File size cannot exceed 100MB!')
        return false
      }
      uploading.value = true
      uploadProgress.value = 0
      progressText.value = 'Preparing upload...'
      return true
    }

    const handleProgress = (event) => {
      uploadProgress.value = Math.round(event.percent)
      if (uploadProgress.value < 100) {
        progressText.value = `Upload progress: ${uploadProgress.value}%`
      } else {
        progressText.value = 'Analyzing genome...'
      }
    }

    const progressFormat = (percentage) => {
      if (percentage === 100) {
        return 'Processing'
      }
      return `${percentage}%`
    }

    const handleSuccess = (response) => {
      uploading.value = false
      results.value = response.results
      resultFileUrl.value = response.result_file

      // Parse TSV format results
      const lines = response.results.split('\n')
      parsedResults.value = lines.slice(1)
        .filter(line => line.trim())
        .map(line => {
          const values = line.split('\t')
          return {
            name: values[0],
            completeness: parseFloat(values[1]).toFixed(2),
            contamination: parseFloat(values[2]).toFixed(2),
            quality: values[3]
          }
        })
      ElMessage.success('Analysis complete!')
    }

    const handleError = (error) => {
      uploading.value = false
      console.error('Upload error:', error)
      ElMessage.error(error.response?.data?.error || 'Genome analysis failed, please try again')
    }

    const downloadResults = async () => {
      if (!resultFileUrl.value) return
      
      downloading.value = true
      try {
        const response = await fetch(resultFileUrl.value)
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `checkm2_results_${new Date().toISOString().slice(0,10)}.tsv`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      } catch (error) {
        ElMessage.error('Download failed, please try again')
        console.error('Download error:', error)
      } finally {
        downloading.value = false
      }
    }

    const getCompletenessColor = (value) => {
      value = Number(value)
      if (value >= 90) return '#67C23A'
      if (value >= 70) return '#E6A23C'
      return '#F56C6C'
    }

    const getContaminationColor = (value) => {
      value = Number(value)
      if (value <= 5) return '#67C23A'
      if (value <= 10) return '#E6A23C'
      return '#F56C6C'
    }

    const getQualityTagType = (quality) => {
      switch (quality.toLowerCase()) {
        case 'high':
          return 'success'
        case 'medium':
          return 'warning'
        case 'low':
          return 'danger'
        default:
          return 'info'
      }
    }

    const getCompletenessTooltip = (value) => {
      value = Number(value)
      if (value >= 90) return 'High Completeness (≥90%)'
      if (value >= 70) return 'Medium Completeness (≥70%)'
      return 'Low Completeness (<70%)'
    }

    const getContaminationTooltip = (value) => {
      value = Number(value)
      if (value <= 5) return 'Low Contamination (≤5%)'
      if (value <= 10) return 'Medium Contamination (≤10%)'
      return 'High Contamination (>10%)'
    }

    const getQualityTooltip = (quality) => {
      const tooltips = {
        'high': 'High Quality: Completeness ≥90% and Contamination ≤5%',
        'medium': 'Medium Quality: Completeness ≥70% and Contamination ≤10%',
        'low': 'Low Quality: Completeness <70% or Contamination >10%'
      }
      return tooltips[quality.toLowerCase()] || 'Unknown Quality'
    }

    const getQualityLabel = (quality) => {
      const labels = {
        'high': 'High Quality',
        'medium': 'Medium',
        'low': 'Low Quality'
      }
      return labels[quality.toLowerCase()] || quality
    }

    return {
      results,
      parsedResults,
      uploading,
      uploadProgress,
      progressText,
      resultFileUrl,
      downloading,
      beforeUpload,
      handleProgress,
      handleSuccess,
      handleError,
      downloadResults,
      progressFormat,
      getCompletenessColor,
      getContaminationColor,
      getQualityTagType,
      getCompletenessTooltip,
      getContaminationTooltip,
      getQualityTooltip,
      getQualityLabel
    }
  }
}
</script>

<style scoped>
.checkm2-container {
  padding: 20px;
}

.content-section {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 20px;
}

h1 {
  color: #2c3e50;
  margin-bottom: 20px;
  text-align: center;
}

.description {
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.description h2 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 1.5rem;
}

.description p {
  color: #666;
  line-height: 1.6;
}

.features {
  margin-top: 20px;
}

.features h3 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 1.2rem;
}

.features ul {
  list-style-type: none;
  padding-left: 0;
}

.features li {
  color: #666;
  margin-bottom: 8px;
  padding-left: 20px;
  position: relative;
}

.features li::before {
  content: "•";
  color: #4169E1;
  position: absolute;
  left: 0;
}

.upload-section {
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.upload-section h2 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 1.5rem;
}

.upload-info {
  color: #666;
  margin-bottom: 20px;
}

.progress-section {
  margin-top: 20px;
}

.progress-text {
  text-align: center;
  color: #666;
  margin-top: 10px;
}

.results-section {
  margin-top: 30px;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.results-header h2 {
  color: #2c3e50;
  font-size: 1.5rem;
  margin: 0;
}

.quality-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}

:deep(.el-upload-dragger) {
  width: 100%;
  height: 200px;
}

:deep(.el-upload__tip) {
  color: #666;
}

:deep(.el-progress-bar__inner) {
  transition: width 0.3s ease;
}

.citation-section {
  margin-top: 40px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.citation-section h3 {
  color: #2c3e50;
  margin-bottom: 15px;
  font-size: 1.3rem;
}

.citation-content {
  color: #666;
  line-height: 1.6;
}

.citation-box {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 15px;
  margin: 10px 0;
}

.citation-box a {
  color: #409EFF;
  text-decoration: none;
}

.citation-box a:hover {
  text-decoration: underline;
}

.license-text {
  margin-top: 10px;
  font-style: italic;
}

.additional-info {
  margin: 15px 0;
  color: #666;
  line-height: 1.6;
}

.model-list {
  list-style-type: none;
  padding-left: 20px;
  margin: 15px 0;
}

.model-list li {
  margin-bottom: 10px;
  position: relative;
  padding-left: 20px;
}

.model-list li::before {
  content: "•";
  color: #4169E1;
  position: absolute;
  left: 0;
}

.license-info {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #e4e7ed;
}

.usage-note {
  margin-top: 10px;
  font-size: 0.9em;
  color: #909399;
}

.paper-citation {
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px dashed #e4e7ed;
  line-height: 1.6;
}

.paper-citation em {
  font-style: italic;
}

.repo-citation {
  margin-bottom: 0;
  line-height: 1.6;
}

.citation-box strong {
  color: #2c3e50;
}

.citation-box a {
  word-break: break-all;
}
</style> 